import { forwardRef } from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cn } from "../lib/cn";
import { CheckIcon, RemoveIcon } from "../lib/icons";

export interface CheckboxProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    "children"
  > {
  /** Error state — red border + aria-invalid. */
  invalid?: boolean;
}

/**
 * 20px checkbox with checked + indeterminate states (pass
 * `checked="indeterminate"` / expect it back from `onCheckedChange`).
 */
export const Checkbox = forwardRef<
  React.ComponentRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, invalid = false, ...props }, ref) => {
  return (
    <CheckboxPrimitive.Root
      ref={ref}
      aria-invalid={invalid ? true : undefined}
      className={cn(
        // Base: 20px square, rounded-md, strong border
        "group inline-flex size-5 shrink-0 items-center justify-center rounded-md border-2 border-border-control bg-surface-default outline-none transition-colors duration-150",
        // Focus
        "focus-visible:ring-2 focus-visible:ring-border-focused focus-visible:ring-offset-1 focus-visible:ring-offset-surface-default",
        // Checked / indeterminate fills
        "data-[state=checked]:border-surface-primary data-[state=checked]:bg-surface-primary data-[state=indeterminate]:border-surface-primary data-[state=indeterminate]:bg-surface-primary",
        // Disabled
        "disabled:cursor-not-allowed disabled:opacity-38",
        invalid &&
          "border-surface-error data-[state=checked]:border-surface-error data-[state=indeterminate]:border-surface-error",
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="flex items-center justify-center text-on-primary">
        <CheckIcon
          aria-hidden="true"
          className="hidden size-3.5 group-data-[state=checked]:block"
        />
        <RemoveIcon
          aria-hidden="true"
          className="hidden size-3.5 group-data-[state=indeterminate]:block"
        />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});
Checkbox.displayName = "Checkbox";
